import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './styles/App.css';
import './styles/theme.css';

import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import LibraryPage from './pages/LibraryPage';
import PlaylistsPage from './pages/PlaylistsPage';
import SharePage from './pages/SharePage';
import StatsPage from './pages/StatsPage';
import ProfilePage from './pages/ProfilePage';
import SubmitPage from './pages/SubmitPage';
import MessagesPage from './pages/MessagesPage';
import RequestsPage from './pages/RequestsPage';
import RankingsPage from './pages/RankingsPage';
import PartyPage from './pages/PartyPage';
import Header from './components/Header';
import Player from './components/Player';

axios.defaults.withCredentials = true;
axios.defaults.baseURL = 'https://disney-music.gpworld.fr';

const RADIO_TRACK = {
  id: 'radio-disney-village',
  title: 'Radio Disney Village',
  artist: 'En direct',
  file_path: 'https://webradio.ice.infomaniak.ch/webradio-128.mp3',
  isRadio: true
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);

  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem('disney-music-theme') || 'dark');
  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
    localStorage.setItem('disney-music-theme', theme);
  }, [theme]);
  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/auth/check');
      setUser(response.data.user);
    } catch (error) {
      console.log('Non authentifié');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const playTrack = (track, albumTracks = []) => {
    setCurrentTrack(track);
    setPlaylist(albumTracks);
    setIsPlaying(true);
  };

  // Ajouter une piste à la file d'attente
  const addToQueue = (track) => {
    setQueue(prev => {
      // Éviter les doublons
      if (prev.some(t => t.id === track.id)) return prev;
      return [...prev, track];
    });
  };

  // Supprimer de la file d'attente
  const removeFromQueue = (trackId) => {
    setQueue(prev => prev.filter(t => t.id !== trackId));
  };

  // Vider la file d'attente
  const clearQueue = () => setQueue([]);

  const shufflePlay = async () => {
    try {
      const response = await axios.get('/api/random/playlist?count=30');
      const tracks = response.data;
      if (tracks.length > 0) {
        setCurrentTrack(tracks[0]);
        setPlaylist(tracks);
        setQueue([]);
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Erreur lecture aléatoire:', error);
    }
  };

  const toggleRadio = () => {
    if (currentTrack?.isRadio) {
      closePlayer();
    } else {
      setCurrentTrack(RADIO_TRACK);
      setPlaylist([]);
      setQueue([]);
      setIsPlaying(true);
    }
  };

  const playNext = () => {
    if (currentTrack?.isRadio) return;

    // D'abord lire la file d'attente
    if (queue.length > 0) {
      const nextTrack = queue[0];
      setQueue(prev => prev.slice(1));
      setCurrentTrack(nextTrack);
      return;
    }

    // Sinon continuer la playlist
    if (playlist.length === 0) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack?.id);
    if (currentIndex < playlist.length - 1) {
      setCurrentTrack(playlist[currentIndex + 1]);
    }
  };

  const playPrevious = () => {
    if (currentTrack?.isRadio || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(t => t.id === currentTrack?.id);
    if (currentIndex > 0) {
      setCurrentTrack(playlist[currentIndex - 1]);
    }
  };

  const closePlayer = () => {
    setCurrentTrack(null);
    setPlaylist([]);
    setQueue([]);
    setIsPlaying(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        <Routes>
          <Route path="/share/:token" element={<SharePage />} />

          <Route path="*" element={
            !user ? <LoginPage /> : (
              <>
                <Header 
                  user={user} 
                  onToggleRadio={toggleRadio} 
                  isRadioPlaying={currentTrack?.isRadio && isPlaying}
                  theme={theme}
                  toggleTheme={toggleTheme}
                  onShuffle={shufflePlay}
                />
                
                <main className="main-content">
                  <Routes>
                    <Route 
                      path="/" 
                      element={<HomePage playTrack={playTrack} addToQueue={addToQueue} />} 
                    />
                    
                    <Route 
                      path="/library" 
                      element={<LibraryPage playTrack={playTrack} addToQueue={addToQueue} />} 
                    />

                    <Route 
                      path="/playlists" 
                      element={<PlaylistsPage playTrack={playTrack} addToQueue={addToQueue} />} 
                    />
                    
                    {(user.role === 'admin' || user.role === 'moderator') && (
                      <Route 
                        path="/admin" 
                        element={<AdminPage />} 
                      />
                    )}
                    
                    <Route path="*" element={<Navigate to="/" replace />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/:username" element={<ProfilePage />} />
              <Route path="/submit" element={<SubmitPage user={user} />} />
              <Route path="/messages" element={<MessagesPage user={user} />} />
              <Route path="/requests" element={<RequestsPage user={user} />} />
              <Route path="/rankings" element={<RankingsPage playTrack={playTrack} />} />
              <Route path="/party" element={<PartyPage user={user} playTrack={playTrack} />} />
                  </Routes>
                </main>

                {currentTrack && (
                  <Player
                    currentTrack={currentTrack}
                    playlist={playlist}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    onNext={playNext}
                    onPrevious={playPrevious}
                    onTrackSelect={setCurrentTrack}
                    onClose={closePlayer}
                    queue={queue}
                    onRemoveFromQueue={removeFromQueue}
                    onClearQueue={clearQueue}
                    addToQueue={addToQueue}
                  />
                )}
              </>
            )
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
